/** Lecture Web Audio : passage simple, boucle unique, ou plusieurs boucles superposées. */
import { getContext, unlock } from './context';

export interface Region {
  start: number;
  end: number;
}

export class Player {
  private sources: AudioBufferSourceNode[] = [];
  private gain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private ctxStart = 0;
  private offset = 0;
  private span: Region | null = null;
  private looping = false;
  onChange?: () => void;

  /**
   * Bus master : gain de compensation + limiteur avant la sortie.
   * - La sortie Web Audio est plus faible que le natif (surtout iOS) → makeup gain.
   * - Empiler plusieurs pistes peut saturer → le compresseur en mode limiteur
   *   plafonne les crêtes proprement au lieu de clipper.
   */
  private out(): GainNode {
    const ctx = getContext();
    if (!this.gain) {
      this.gain = ctx.createGain();
      this.gain.gain.value = 1.4; // makeup : remonte le niveau perçu
      this.limiter = ctx.createDynamicsCompressor();
      this.limiter.threshold.value = -3;
      this.limiter.knee.value = 0;
      this.limiter.ratio.value = 20;
      this.limiter.attack.value = 0.003;
      this.limiter.release.value = 0.25;
      this.gain.connect(this.limiter);
      this.limiter.connect(ctx.destination);
    }
    return this.gain;
  }

  get isPlaying(): boolean {
    return this.sources.length > 0;
  }

  /** Position de lecture en secondes dans le buffer source. */
  get time(): number {
    if (!this.sources.length) return this.offset;
    const ctx = getContext();
    let t = this.offset + (ctx.currentTime - this.ctxStart);
    if (this.span && this.looping) {
      const len = this.span.end - this.span.start;
      if (len > 0.01) t = this.span.start + ((t - this.span.start) % len);
    }
    return t;
  }

  async play(buffer: AudioBuffer, region?: Region, loop = false): Promise<void> {
    await unlock();
    this.stop();

    const ctx = getContext();
    const start = Math.max(0, region?.start ?? 0);
    const end = Math.min(buffer.duration, region?.end ?? buffer.duration);
    if (end - start < 0.01) return;

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.out());

    if (loop) {
      src.loop = true;
      src.loopStart = start;
      src.loopEnd = end;
      src.start(0, start);
    } else {
      src.start(0, start, end - start);
      src.onended = () => {
        if (this.sources.indexOf(src) !== -1) this.stop();
      };
    }

    this.sources = [src];
    this.span = { start, end };
    this.looping = loop;
    this.offset = start;
    this.ctxStart = ctx.currentTime;
    this.onChange?.();
  }

  /** Toutes les boucles cochées, en même temps et en boucle. */
  async playRegions(buffer: AudioBuffer, regions: Region[]): Promise<void> {
    await unlock();
    this.stop();
    if (!regions.length) return;

    const ctx = getContext();
    const at = ctx.currentTime + 0.05;

    for (const r of regions) {
      const start = Math.max(0, r.start);
      const end = Math.min(buffer.duration, r.end);
      if (end - start < 0.01) continue;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.loopStart = start;
      src.loopEnd = end;
      src.connect(this.out());
      src.start(at, start);
      this.sources.push(src);
    }

    const first = regions[0];
    this.span = { start: first.start, end: first.end };
    this.looping = true;
    this.offset = first.start;
    this.ctxStart = at;
    this.onChange?.();
  }

  /** Plusieurs pistes (buffers différents), en superposition, avec volume par piste. */
  async playBuffers(items: { buffer: AudioBuffer; volume: number }[], loop = true): Promise<void> {
    await unlock();
    this.stop();
    if (!items.length) return;

    const ctx = getContext();
    const at = ctx.currentTime + 0.05;
    let maxDur = 0;

    for (const it of items) {
      if (!it.buffer || it.buffer.duration < 0.01) continue;
      const src = ctx.createBufferSource();
      src.buffer = it.buffer;
      src.loop = loop;
      const g = ctx.createGain();
      g.gain.value = Math.max(0, Math.min(1, it.volume));
      src.connect(g);
      g.connect(this.out());
      src.start(at);
      this.sources.push(src);
      if (it.buffer.duration > maxDur) maxDur = it.buffer.duration;
    }

    this.span = { start: 0, end: maxDur };
    this.looping = loop;
    this.offset = 0;
    this.ctxStart = at;
    this.onChange?.();
  }

  stop(): void {
    const had = this.sources.length > 0;
    for (const s of this.sources) {
      try {
        s.onended = null;
        s.stop();
      } catch {
        /* déjà arrêtée */
      }
      s.disconnect();
    }
    this.sources = [];
    this.span = null;
    this.looping = false;
    if (had) this.onChange?.();
  }

  seek(t: number): void {
    this.offset = t;
    if (!this.sources.length) this.onChange?.();
  }
}
